import { cn } from '@/lib/utils';
import { Loader2, RefreshCw, Sparkles, Trash2, UploadCloud } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { SectionHeader } from '@/components/ui/section-header';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table';
import { TableSkeleton } from '@/components/ui/table-skeleton';

interface SkillRow {
    skillId: string;
    displayTitle: string;
    source: string;
}

type StagedFileStatus = 'uploading' | 'error';

interface StagedFile {
    id: string;
    file: File;
    status: StagedFileStatus;
    error?: string;
}

const ZIP_ACCEPT = {
    'application/zip': ['.zip'],
    'application/octet-stream': ['.skill']
};

export const SkillsTab: React.FC = () => {
    const [skills, setSkills] = useState<SkillRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [updatingId, setUpdatingId] = useState<string | null>(null);

    const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
    const nextStagedId = useRef(0);
    const updateInputRef = useRef<HTMLInputElement>(null);
    const updateTargetId = useRef<string | null>(null);

    const [deleteTarget, setDeleteTarget] = useState<SkillRow | null>(null);

    const fetchSkills = (opts: { silent?: boolean } = {}) => {
        if (!opts.silent) setLoading(true);
        fetch('/api/skills', { credentials: 'include' })
            .then((res) => (res.ok ? res.json() : []))
            .then((data) => {
                const rows: SkillRow[] = (Array.isArray(data) ? data : [])
                    .filter((s: any) => s.source !== 'anthropic')
                    .map((s: any) => ({
                        skillId: s.skill_id,
                        displayTitle: s.display_title,
                        source: s.source
                    }));
                setSkills(rows);
            })
            .catch(() => {
                if (!opts.silent) setSkills([]);
                toast.error('Failed to load skills');
            })
            .finally(() => {
                if (!opts.silent) setLoading(false);
            });
    };

    useEffect(() => fetchSkills(), []);

    const uploadSingleFile = async (file: File) => {
        const displayTitle = file.name.replace(/\.(skill|zip)$/i, '');
        const formData = new FormData();
        formData.append('file', file);
        formData.append('displayTitle', displayTitle);
        const res = await fetch('/api/skills/upload', {
            method: 'POST',
            credentials: 'include',
            body: formData
        });
        if (!res.ok) {
            const j = await res.json().catch(() => null);
            throw new Error(j?.detail || 'Failed to upload skill');
        }
    };

    const uploadStagedFile = (entry: StagedFile) => {
        uploadSingleFile(entry.file)
            .then(() => {
                setStagedFiles((prev) => prev.filter((f) => f.id !== entry.id));
                fetchSkills({ silent: true });
                toast.success('Skill uploaded!');
            })
            .catch((e: any) => {
                setStagedFiles((prev) =>
                    prev.map((f) =>
                        f.id === entry.id
                            ? {
                                  ...f,
                                  status: 'error',
                                  error: e?.message || 'Failed to upload skill'
                              }
                            : f
                    )
                );
            });
    };

    const onDrop = useCallback(
        (accepted: File[], rejections: FileRejection[]) => {
            const acceptedEntries: StagedFile[] = accepted.map((file) => ({
                id: `s-${nextStagedId.current++}`,
                file,
                status: 'uploading'
            }));
            const rejectedEntries: StagedFile[] = rejections.map((r) => ({
                id: `s-${nextStagedId.current++}`,
                file: r.file,
                status: 'error',
                error:
                    r.errors[0]?.message || 'Only .skill or .zip files are accepted'
            }));
            setStagedFiles((prev) => [
                ...prev,
                ...acceptedEntries,
                ...rejectedEntries
            ]);
            acceptedEntries.forEach(uploadStagedFile);
        },
        []
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple: true,
        accept: ZIP_ACCEPT
    });

    const removeStagedFile = (id: string) =>
        setStagedFiles((prev) => prev.filter((f) => f.id !== id));

    const handleUpdateClick = (skillId: string) => {
        updateTargetId.current = skillId;
        updateInputRef.current?.click();
    };

    const handleUpdateFileSelected = async (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = e.target.files?.[0];
        const skillId = updateTargetId.current;
        e.target.value = '';
        if (!file || !skillId) return;

        setUpdatingId(skillId);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await fetch(
                `/api/skills/${encodeURIComponent(skillId)}`,
                { method: 'PUT', credentials: 'include', body: formData }
            );
            if (!res.ok) {
                const j = await res.json().catch(() => null);
                throw new Error(j?.detail || 'Failed to update skill');
            }
            toast.success('Skill updated!');
        } catch (e: any) {
            toast.error(e?.message || 'Failed to update skill');
        } finally {
            setUpdatingId(null);
        }
    };

    const handleDelete = async (skill: SkillRow) => {
        setDeleteTarget(null);
        setDeletingId(skill.skillId);
        try {
            const res = await fetch(
                `/api/skills/${encodeURIComponent(skill.skillId)}`,
                { method: 'DELETE', credentials: 'include' }
            );
            if (!res.ok) {
                const j = await res.json().catch(() => null);
                throw new Error(j?.detail || 'Failed to delete skill');
            }
            setSkills((prev) => prev.filter((s) => s.skillId !== skill.skillId));
            toast.success('Skill deleted.');
        } catch (e: any) {
            toast.error(e?.message || 'Failed to delete skill');
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="space-y-8">
            <section className="space-y-6">
                <SectionHeader
                    title="Skills"
                    description="Custom skills (a zipped SKILL.md package) available to attach to agents, alongside Anthropic's built-in catalog."
                />

                <input
                    ref={updateInputRef}
                    type="file"
                    accept=".zip,.skill"
                    className="hidden"
                    onChange={handleUpdateFileSelected}
                />

                {loading && <TableSkeleton columns={3} />}

                {!loading && (
                    <div
                        {...getRootProps()}
                        className={cn(
                            'flex flex-col justify-center items-center gap-3 bg-muted p-8 border-2 border-dashed rounded-xl text-center transition-colors cursor-pointer',
                            isDragActive
                                ? 'border-foreground bg-foreground/5'
                                : 'bg-muted/5 dark:bg-muted/10 border-muted-foreground/20 dark:border-muted-foreground/10 hover:border-foreground/40'
                        )}
                    >
                        <input {...getInputProps()} />
                        <div className="flex justify-center items-center bg-muted border border-border rounded-lg size-12 text-foreground">
                            <UploadCloud className="size-6" />
                        </div>
                        <div className="flex flex-col items-center gap-0.5">
                            <p className="font-medium text-foreground text-sm">
                                Click to{' '}
                                <span className="text-foreground underline underline-offset-2">
                                    upload
                                </span>{' '}
                                or drag and drop
                            </p>
                            <p className="text-muted-foreground text-xs">
                                .skill or .zip, with a SKILL.md at its root
                            </p>
                        </div>
                    </div>
                )}

                {!loading && stagedFiles.length > 0 && (
                    <div className="space-y-2 max-h-64 overflow-y-auto scrollbar-hidden">
                        {stagedFiles.map((sf) => (
                            <div
                                key={sf.id}
                                className="flex items-center gap-3 p-3 border border-border rounded-lg"
                            >
                                <div
                                    className={cn(
                                        'flex justify-center items-center rounded-md size-9 shrink-0',
                                        sf.status === 'error'
                                            ? 'bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-500'
                                            : 'bg-muted text-muted-foreground'
                                    )}
                                >
                                    <Sparkles className="size-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm truncate">
                                        {sf.file.name}
                                    </div>
                                    {sf.status === 'error' && (
                                        <div className="text-red-600 dark:text-red-500 text-xs">
                                            {sf.error}
                                        </div>
                                    )}
                                </div>
                                <div className="shrink-0">
                                    {sf.status === 'uploading' && (
                                        <Loader2 className="m-2 size-4 text-muted-foreground animate-spin" />
                                    )}
                                    {sf.status === 'error' && (
                                        <Button
                                            variant="ghost"
                                            size="icon-sm"
                                           
                                            onClick={() => removeStagedFile(sf.id)}
                                        >
                                            <Trash2 />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {!loading && skills.length > 0 && (
                    <div>
                        <Table variant="fluid">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Display title</TableHead>
                                    <TableHead className="text-sm">
                                        Skill ID
                                    </TableHead>
                                    <TableHead className="text-right">
                                        {/* Actions */}
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {skills.map((skill) => (
                                    <TableRow key={skill.skillId}>
                                        <TableCell className="font-medium">
                                            <div className="flex items-center gap-2.5">
                                                <div className="flex justify-center items-center bg-muted rounded-md size-8 text-muted-foreground shrink-0">
                                                    <Sparkles className="size-4" />
                                                </div>
                                                {skill.displayTitle}
                                            </div>
                                        </TableCell>
                                        <TableCell className="font-mono text-muted-foreground text-sm">
                                            {skill.skillId}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end items-center gap-2">
                                                <Button
                                                    variant="tertiary"
                                                    size="default"
                                                    onClick={() =>
                                                        handleUpdateClick(
                                                            skill.skillId
                                                        )
                                                    }
                                                    disabled={
                                                        updatingId ===
                                                        skill.skillId
                                                    }
                                                    className="text-sm"
                                                >
                                                    {updatingId ===
                                                    skill.skillId ? (
                                                        <Loader2 className="animate-spin" />
                                                    ) : (
                                                        <RefreshCw />
                                                    )}
                                                    Replace
                                                </Button>
                                                <Button
                                                    variant="destructive"
                                                    size="default"
                                                    onClick={() =>
                                                        setDeleteTarget(skill)
                                                    }
                                                    disabled={
                                                        deletingId ===
                                                        skill.skillId
                                                    }
                                                    className="text-sm"
                                                >
                                                    {deletingId ===
                                                    skill.skillId ? (
                                                        <Loader2 className="animate-spin" />
                                                    ) : (
                                                        <Trash2 />
                                                    )}
                                                    Delete
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </section>

            <AlertDialog
                open={!!deleteTarget}
                onOpenChange={(open) => !open && setDeleteTarget(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Delete "{deleteTarget?.displayTitle}"?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            This removes the skill from Anthropic and from
                            every agent that has it attached. This cannot be
                            undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction variant="destructive" onClick={() =>
                                    deleteTarget && handleDelete(deleteTarget)
                                }>
                                Delete permanently
                            </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};
